import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  BookOpen,
  Scale,
  Building2,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { getMethodology } from '../services/methodologyApi';
import type {
  MethodologyResponse,
  MethodologyResult,
  MethodologyPosture,
  EngineOutput,
  ProjectionTrendOutput,
  TwoPeriodOutput,
  BottomUpOutput,
  DualMethodOutput,
  EpiOutput,
  CapacityCeilingOutput,
  NonRecurringDetectorOutput,
  CatchUpSalesOutput,
  MonthlyLimitCapOutput,
  ExtraExpenseCapOutput,
} from '../types/methodology';
import { formatCurrency } from '../utils/formatters';
import { humanizeMethodId } from '../utils/methodLabels';
import { useWorkspaceRole } from '../hooks/useWorkspaceRole';
import { MethodDossierSection } from '../components/methodology/MethodDossierSection';
import { useCanonicalNet } from '../hooks/useCanonicalNet';

// ---------------------------------------------------------------------------
// Presentation helpers
// ---------------------------------------------------------------------------

const POSTURE_BADGE: Record<MethodologyPosture, { label: string; style: React.CSSProperties }> = {
  conservative: {
    label: 'Conservative floor',
    style: {
      background: 'var(--color-bg-elevated)',
      color: 'var(--color-text-secondary)',
      borderColor: 'var(--color-border-sub)',
    },
  },
  neutral: {
    label: 'Neutral',
    style: {
      background: 'var(--color-bg-elevated)',
      color: 'var(--color-text-muted)',
      borderColor: 'var(--color-border-sub)',
    },
  },
  aggressive: {
    // Posture is CLASSIFICATION (structure), not adverse or wayfinding → hueless
    // (K3 review 2026-08-23; the label text carries the meaning, not a hue). If
    // K3 later rules "aggressive" a needs-human signal, that is a 1-line follow-up.
    label: 'Aggressive',
    style: {
      background: 'var(--color-bg-elevated)',
      color: 'var(--color-text-secondary)',
      borderColor: 'var(--color-border)',
    },
  },
  conditional: {
    label: 'Conditional lever',
    style: {
      background: 'var(--color-bg-elevated)',
      color: 'var(--color-text-secondary)',
      borderColor: 'var(--color-border)',
    },
  },
};

// Low → high so the BI section reads as a defensible range.
const POSTURE_ORDER: Record<MethodologyPosture, number> = {
  conservative: 0,
  neutral: 1,
  aggressive: 2,
  conditional: 3,
};

/** null value (engine produced no output) renders as an em dash per the
 *  financial-data convention; real figures use the shared currency formatter. */
function renderValue(value: number | null): string {
  return value === null ? '—' : formatCurrency(value);
}

// Engines whose raw output has a drill-down renderer below. A method row gets a
// toggle only when its source_engine is in this set AND the slate carried that
// engine's output (method_details). `property` is intentionally absent — its
// breakdown (RCV/ACV/depreciation/ordinance/debris) already IS the per-row
// property section, so a drill-down would be redundant.
const DETAILED_ENGINES = new Set<string>([
  'projection_trend',
  'two_period',
  'bottom_up',
  'dual_method',
  'epi',
]);

function fmtMonth(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, '0')}`;
}

function StatChip({ label, value }: { label: string; value: string }) {
  return (
    <span
      className="cs-chip inline-flex items-baseline gap-1 rounded px-2 py-1"
      style={{
        background: 'var(--color-bg-surface)',
        boxShadow: 'inset 0 0 0 1px var(--color-border-sub)',
      }}
    >
      <span style={{ color: 'var(--color-text-muted)' }}>{label}</span>
      <span className="cs-figure" style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>{value}</span>
    </span>
  );
}

function DetailTable({ columns, rows }: { columns: string[]; rows: string[][] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full" data-testid="methodology-detail-table">
        <thead>
          <tr style={{ borderBottom: '1px solid var(--color-border-sub)' }}>
            {columns.map((c, i) => (
              <th
                key={c}
                className={`cs-label py-1.5 ${i === 0 ? 'text-left' : 'text-right'}`}
              >
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, ri) => (
            <tr key={ri} className="last:border-b-0" style={{ borderBottom: '1px solid var(--color-border-faint)' }}>
              {r.map((cell, ci) => (
                <td
                  key={ci}
                  className={
                    ci === 0
                      ? 'cs-meta py-1.5 text-left'
                      : 'cs-figure py-1.5 text-right tabular-nums'
                  }
                  style={ci === 0 ? undefined : { color: 'var(--color-text-primary)' }}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function KeyGrid({ items }: { items: { label: string; value: string }[] }) {
  return (
    <dl
      className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3"
      data-testid="methodology-detail-grid"
    >
      {items.map((it) => (
        <div key={it.label} className="flex flex-col gap-0.5">
          <dt className="cs-label">{it.label}</dt>
          <dd className="cs-figure tabular-nums" style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>
            {it.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function ToneBadge({
  label,
  tone,
}: {
  label: string;
  tone: 'green' | 'amber' | 'red' | 'gray';
}) {
  // Hue law (K3 review 2026-08-23): green→hueless verified (no green), amber→the
  // needs-human marker, red→crimson (adverse). Transparent fills; hue on the ring
  // + text, never a wash.
  const toneStyle: React.CSSProperties = {
    green: {
      borderColor: 'var(--color-border)',
      background: 'transparent',
      color: 'var(--color-verified)',
    },
    amber: {
      borderColor: 'var(--color-border)',
      background: 'transparent',
      color: 'var(--color-needs-human)',
    },
    red: {
      borderColor: 'var(--color-blocker)',
      background: 'transparent',
      color: 'var(--color-blocker)',
    },
    gray: {
      borderColor: 'var(--color-border-sub)',
      background: 'transparent',
      color: 'var(--color-text-muted)',
    },
  }[tone];
  return (
    <span
      className="cs-chip inline-flex items-center rounded-[2px] border px-2 py-0.5"
      style={toneStyle}
    >
      {label}
    </span>
  );
}

/** Month-by-month detail for the methodology engines with rich tabular output.
 *  Reads from the already-loaded method_details — no extra fetch. */
function MethodDetailTable({
  engineId,
  details,
  reportId,
  canAttest,
}: {
  engineId: string;
  details: EngineOutput;
  reportId?: string;
  canAttest?: boolean;
}) {
  if (engineId === 'projection_trend') {
    const o = details as unknown as ProjectionTrendOutput;
    const rows = (o.monthly_projections ?? []).map((m) => [
      fmtMonth(m.year, m.month),
      formatCurrency(m.projected),
      formatCurrency(m.actual),
      formatCurrency(m.loss),
    ]);
    return (
      <div className="space-y-3" data-testid="methodology-detail-projection_trend">
        <div className="flex flex-wrap gap-1.5">
          {o.r_squared !== undefined && (
            <StatChip
              label="R²"
              value={`${o.r_squared.toFixed(2)}${o.r_squared_weak ? ' · weak' : ''}`}
            />
          )}
          {o.trend_slope !== undefined && (
            <StatChip label="slope/mo" value={formatCurrency(o.trend_slope)} />
          )}
          {o.confidence_interval && (
            <StatChip
              label="95% CI"
              value={`${formatCurrency(o.confidence_interval.low)} – ${formatCurrency(
                o.confidence_interval.high,
              )}`}
            />
          )}
          {o.months_in_trend !== undefined && (
            <StatChip label="fit" value={`${o.months_in_trend} mo`} />
          )}
        </div>
        {rows.length > 0 ? (
          <DetailTable columns={['Month', 'Projected', 'Actual', 'Loss']} rows={rows} />
        ) : (
          <p className="cs-meta" style={{ color: 'var(--color-text-dim)' }}>No monthly projections in this run.</p>
        )}
      </div>
    );
  }

  if (engineId === 'two_period') {
    const o = details as unknown as TwoPeriodOutput;
    const rows = (o.monthly_comparisons ?? []).map((m) => [
      fmtMonth(m.year, m.month),
      formatCurrency(m.prior_avg),
      formatCurrency(m.actual),
      formatCurrency(m.loss),
    ]);
    return (
      <div className="space-y-3" data-testid="methodology-detail-two_period">
        <div className="flex flex-wrap gap-1.5">
          {o.years_averaged !== undefined && (
            <StatChip label="prior yrs" value={String(o.years_averaged)} />
          )}
          {o.seasonality_cv !== undefined && (
            <StatChip
              label="seasonality CV"
              value={`${o.seasonality_cv.toFixed(2)}${
                o.seasonality_detected ? ' · detected' : ''
              }`}
            />
          )}
        </div>
        {rows.length > 0 ? (
          <DetailTable
            columns={['Month', 'Prior-yr avg', 'Actual', 'Loss']}
            rows={rows}
          />
        ) : (
          <p className="cs-meta" style={{ color: 'var(--color-text-dim)' }}>No monthly comparisons in this run.</p>
        )}
      </div>
    );
  }

  if (engineId === 'bottom_up') {
    const o = details as unknown as BottomUpOutput;
    const rows = (o.monthly_detail ?? []).map((m) => [
      fmtMonth(m.year, m.month),
      formatCurrency(m.but_for_net_income),
      formatCurrency(m.actual_net_income),
      formatCurrency(m.lost_net_income),
      formatCurrency(m.total_month_loss),
    ]);
    return (
      <div className="space-y-3" data-testid="methodology-detail-bottom_up">
        <KeyGrid
          items={[
            { label: 'But-for NI', value: formatCurrency(o.but_for_net_income) },
            { label: 'Actual NI', value: formatCurrency(o.actual_net_income) },
            { label: 'Lost NI', value: formatCurrency(o.lost_net_income) },
            {
              label: 'Continuing exp.',
              value: formatCurrency(o.total_continuing_expenses),
            },
          ]}
        />
        {rows.length > 0 ? (
          <DetailTable
            columns={['Month', 'But-for NI', 'Actual NI', 'Lost NI', 'Month loss']}
            rows={rows}
          />
        ) : (
          <p className="cs-meta" style={{ color: 'var(--color-text-dim)' }}>No monthly detail in this run.</p>
        )}
      </div>
    );
  }

  if (engineId === 'dual_method') {
    const o = details as unknown as DualMethodOutput;
    return (
      <div className="space-y-3" data-testid="methodology-detail-dual_method">
        <KeyGrid
          items={[
            { label: 'Top-down', value: formatCurrency(o.top_down_loss) },
            { label: 'Bottom-up', value: formatCurrency(o.bottom_up_loss) },
            { label: 'Variance', value: formatCurrency(o.variance) },
            { label: 'Recommended', value: formatCurrency(o.recommended_amount) },
          ]}
        />
        {o.is_within_tolerance !== undefined && (
          <ToneBadge
            label={
              o.is_within_tolerance
                ? 'Methods within tolerance'
                : 'Methods exceed tolerance — reconcile'
            }
            tone={o.is_within_tolerance ? 'green' : 'amber'}
          />
        )}
        {/* The dual-method variance attribution now lives on the SM1 dual_method
            engine-review APPROVE (single source of truth on the engine_reviews row) —
            the accountant sets it when approving the engine at
            /dashboard/accountant/review/{reportId}/dual_method/edit. It is no longer a
            separate methodology-page input. */}
      </div>
    );
  }

  if (engineId === 'epi') {
    const o = details as unknown as EpiOutput;
    const rows = (o.monthly_detail ?? []).map((m) => [
      `Mo ${m.month}`,
      formatCurrency(m.but_for_revenue),
      formatCurrency(m.actual_revenue),
      formatCurrency(m.saved_costs),
      formatCurrency(m.net_loss),
      formatCurrency(m.cumulative_loss),
    ]);
    return (
      <div className="space-y-3" data-testid="methodology-detail-epi">
        <div className="flex flex-wrap items-center gap-1.5">
          {o.epi_status && (
            <ToneBadge
              label={
                o.epi_status === 'CONTESTED'
                  ? 'Contested'
                  : 'Supported (conditional)'
              }
              tone={o.epi_status === 'CONTESTED' ? 'red' : 'amber'}
            />
          )}
          {o.revenue_recovery_date && (
            <StatChip label="recovery" value={String(o.revenue_recovery_date)} />
          )}
        </div>
        {rows.length > 0 ? (
          <DetailTable
            columns={[
              'Month',
              'But-for rev',
              'Actual rev',
              'Saved costs',
              'Net loss',
              'Cumulative',
            ]}
            rows={rows}
          />
        ) : (
          <p className="cs-meta" style={{ color: 'var(--color-text-dim)' }}>No monthly EPI detail in this run.</p>
        )}
      </div>
    );
  }

  return null;
}

// ---------------------------------------------------------------------------
// Coverage caps / adjustments / detectors — read-only panels beneath the slate
// ---------------------------------------------------------------------------

const ADJUSTMENT_META: Record<string, { name: string; blurb: string }> = {
  capacity_ceiling: {
    name: 'Capacity Ceiling',
    blurb:
      'Is the but-for projection within the highest revenue the business has demonstrated?',
  },
  non_recurring_item_detector: {
    name: 'Non-Recurring Item Detector',
    blurb:
      'Months/years whose revenue is anomalous and may warrant exclusion from the base period.',
  },
  catch_up_sales: {
    name: 'Catch-Up Sales Offset',
    blurb:
      'Post-restoration revenue that recovered part of the loss — reduces the claim.',
  },
  monthly_limit_cap: {
    name: 'Monthly Limit of Indemnity (CP 15 10)',
    blurb: 'Per-month coverage cap and the loss it leaves uncovered.',
  },
  extra_expense_cap: {
    name: 'Extra Expense Cap (CP 00 30)',
    blurb: 'How much of the incurred extra expense the policy actually covers.',
  },
};

const ADJUSTMENT_ORDER = [
  'capacity_ceiling',
  'non_recurring_item_detector',
  'catch_up_sales',
  'monthly_limit_cap',
  'extra_expense_cap',
];

function CoverageAdjustmentBody({
  engineId,
  output,
}: {
  engineId: string;
  output: EngineOutput;
}) {
  if (engineId === 'capacity_ceiling') {
    const o = output as unknown as CapacityCeilingOutput;
    return (
      <div className="space-y-3">
        <KeyGrid
          items={[
            { label: 'Demonstrated ceiling', value: formatCurrency(o.demonstrated_ceiling) },
            {
              label: 'Ceiling · loss period',
              value: formatCurrency(o.annualized_ceiling_for_loss_period),
            },
            { label: 'But-for projection', value: formatCurrency(o.but_for_total ?? undefined) },
            { label: 'Excess over ceiling', value: formatCurrency(o.excess_amount) },
          ]}
        />
        {o.exceeds_ceiling !== undefined && (
          <ToneBadge
            label={
              o.exceeds_ceiling
                ? 'Projection exceeds demonstrated ceiling'
                : 'Within demonstrated capacity'
            }
            tone={o.exceeds_ceiling ? 'red' : 'green'}
          />
        )}
      </div>
    );
  }

  if (engineId === 'non_recurring_item_detector') {
    const o = output as unknown as NonRecurringDetectorOutput;
    const rec = o.recommendation;
    const recTone: 'green' | 'amber' | 'red' =
      rec === 'base_period_clean' ? 'green' : rec === 'base_period_unreliable' ? 'red' : 'amber';
    const recLabel =
      rec === 'base_period_clean'
        ? 'Base period clean'
        : rec === 'base_period_unreliable'
        ? 'Base period unreliable'
        : 'Review required';
    const rows = (o.flagged_months ?? []).map((m) => [
      fmtMonth(m.year, m.month),
      formatCurrency(m.revenue),
      (m.z_score ?? 0).toFixed(2),
      m.direction ?? '',
    ]);
    return (
      <div className="space-y-3">
        {rec && <ToneBadge label={recLabel} tone={recTone} />}
        <KeyGrid
          items={[
            { label: 'Months analyzed', value: String(o.months_analyzed ?? '—') },
            { label: 'Flagged months', value: String(o.flagged_months_count ?? 0) },
            { label: 'Flagged years', value: String(o.flagged_years_count ?? 0) },
          ]}
        />
        {rows.length > 0 && (
          <DetailTable columns={['Month', 'Revenue', 'Z-score', 'Direction']} rows={rows} />
        )}
      </div>
    );
  }

  if (engineId === 'catch_up_sales') {
    const o = output as unknown as CatchUpSalesOutput;
    const rows = (o.month_analysis ?? [])
      .filter((m) => m.above_projection)
      .map((m) => [
        fmtMonth(m.year, m.month),
        formatCurrency(m.actual_revenue),
        formatCurrency(m.projected_revenue ?? undefined),
        formatCurrency(m.catch_up_amount ?? undefined),
      ]);
    return (
      <div className="space-y-3">
        <KeyGrid
          items={[
            { label: 'Catch-up offset', value: formatCurrency(o.total_catch_up_offset) },
            {
              label: 'Net loss after offset',
              value: formatCurrency(o.net_loss_after_offset ?? undefined),
            },
            { label: 'Gross catch-up', value: formatCurrency(o.total_catch_up_gross) },
            { label: 'Months w/ catch-up', value: String(o.months_with_catch_up ?? 0) },
          ]}
        />
        {o.has_projections !== undefined && (
          <ToneBadge
            label={
              o.has_projections ? 'Benchmarked against projection' : 'No projection benchmark'
            }
            tone={o.has_projections ? 'green' : 'red'}
          />
        )}
        {rows.length > 0 && (
          <DetailTable columns={['Month', 'Actual', 'Projected', 'Catch-up']} rows={rows} />
        )}
      </div>
    );
  }

  if (engineId === 'monthly_limit_cap') {
    const o = output as unknown as MonthlyLimitCapOutput;
    const binds = o.months_where_cap_binds ?? 0;
    const rows = (o.monthly_caps ?? []).map((m) => [
      fmtMonth(m.year, m.month),
      formatCurrency(m.calculated_loss),
      formatCurrency(m.monthly_cap_amount),
      formatCurrency(m.capped_loss),
      formatCurrency(m.excess_uncovered),
    ]);
    return (
      <div className="space-y-3">
        <KeyGrid
          items={[
            { label: 'Monthly limit', value: o.monthly_limit_fraction_display ?? '—' },
            { label: 'BI limit', value: formatCurrency(o.bi_limit) },
            { label: 'Monthly cap', value: formatCurrency(o.monthly_cap_amount) },
            { label: 'Uncapped loss', value: formatCurrency(o.total_calculated_loss) },
            { label: 'Capped loss', value: formatCurrency(o.total_capped_loss) },
            { label: 'Reduction', value: formatCurrency(o.total_reduction) },
          ]}
        />
        <ToneBadge
          label={
            binds > 0 ? `Cap binds in ${binds} month${binds === 1 ? '' : 's'}` : 'Cap never binds'
          }
          tone={binds > 0 ? 'amber' : 'green'}
        />
        {rows.length > 0 && (
          <DetailTable
            columns={['Month', 'Calculated', 'Cap', 'Capped', 'Excess uncovered']}
            rows={rows}
          />
        )}
      </div>
    );
  }

  if (engineId === 'extra_expense_cap') {
    const o = output as unknown as ExtraExpenseCapOutput;
    const estimated = o.reduction_method === 'estimated_upper_bound';
    return (
      <div className="space-y-3">
        <KeyGrid
          items={[
            { label: 'Actual extra expense', value: formatCurrency(o.actual_extra_expense) },
            { label: 'EE sublimit', value: formatCurrency(o.ee_sublimit ?? undefined) },
            { label: 'BI-loss reduction', value: formatCurrency(o.bi_loss_reduction) },
            { label: 'Covered EE', value: formatCurrency(o.covered_extra_expense) },
            { label: 'Excess (uncovered)', value: formatCurrency(o.excess_extra_expense) },
          ]}
        />
        <div className="flex flex-wrap items-center gap-1.5">
          {o.binding_cap && <StatChip label="binding cap" value={o.binding_cap} />}
          {o.reduction_method && (
            <ToneBadge
              label={estimated ? 'Estimated — needs CPA sign-off' : 'Direct comparison'}
              tone={estimated ? 'amber' : 'green'}
            />
          )}
        </div>
      </div>
    );
  }

  return null;
}

function CoverageAdjustmentPanel({
  engineId,
  output,
}: {
  engineId: string;
  output: EngineOutput;
}) {
  const meta = ADJUSTMENT_META[engineId];
  if (!meta) return null;
  return (
    <div
      className="rounded-[2px] p-5"
      style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}
      data-testid={`coverage-adjustment-${engineId}`}
    >
      <h3 className="cs-h3">{meta.name}</h3>
      <p className="cs-meta mb-3">{meta.blurb}</p>
      <CoverageAdjustmentBody engineId={engineId} output={output} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared atoms
// ---------------------------------------------------------------------------

function MethodRow({
  method,
  details,
  reportId,
  canAttest,
}: {
  method: MethodologyResult;
  details?: EngineOutput;
  reportId?: string;
  canAttest?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const badge = POSTURE_BADGE[method.posture] ?? POSTURE_BADGE.neutral;
  const canExpand =
    !!details &&
    !!method.source_engine &&
    DETAILED_ENGINES.has(method.source_engine);

  return (
    <div
      className="last:border-b-0"
      style={{ borderBottom: '1px solid var(--color-border-faint)' }}
      data-testid={`methodology-method-${method.method_id}`}
    >
      <div className="flex items-start justify-between gap-4 px-5 py-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="cs-h3">{method.label}</h4>
            <span
              className="cs-chip inline-flex items-center rounded-[2px] border px-2 py-0.5"
              style={badge.style}
            >
              {badge.label}
            </span>
            {method.is_default_headline && (
              <span
                className="cs-chip inline-flex items-center rounded-[2px] border px-2 py-0.5"
                style={{
                  borderColor: 'var(--color-border)',
                  background: 'transparent',
                  color: 'var(--color-text-secondary)',
                }}
              >
                Default headline
              </span>
            )}
            {method.contested && (
              <span
                className="cs-chip inline-flex items-center gap-1 rounded-[2px] border px-2 py-0.5"
                style={{
                  borderColor: 'var(--color-blocker)',
                  background: 'transparent',
                  color: 'var(--color-blocker)',
                }}
              >
                <AlertTriangle className="h-3 w-3" />
                Contested
              </span>
            )}
          </div>
          <p className="cs-meta mt-1">{method.authority}</p>
          {Array.isArray(method.assumptions) && method.assumptions.length > 0 && (
            <ul className="mt-1.5 space-y-0.5">
              {method.assumptions.map((a, i) => (
                <li key={i} className="cs-meta" style={{ color: 'var(--color-text-secondary)' }}>• {a}</li>
              ))}
            </ul>
          )}
          {method.notes && (
            <p className="cs-meta mt-1.5 italic" style={{ color: 'var(--color-text-dim)' }}>{method.notes}</p>
          )}
          {canExpand && (
            <button
              type="button"
              onClick={() => setExpanded((e) => !e)}
              data-testid={`methodology-expand-${method.method_id}`}
              aria-expanded={expanded}
              className="cs-chip mt-2 inline-flex items-center gap-1"
              style={{ color: 'var(--color-text-muted)' }}
            >
              {expanded ? (
                <ChevronDown className="h-3.5 w-3.5" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5" />
              )}
              {expanded ? 'Hide' : 'Show'} month-by-month detail
            </button>
          )}
          {((method.strengths?.length ?? 0) + (method.weaknesses?.length ?? 0) + (method.holes?.length ?? 0)) > 0 && (
            <div>
              <MethodDossierSection
                strengths={method.strengths}
                weaknesses={method.weaknesses}
                holes={method.holes}
              />
            </div>
          )}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1.5">
          <span className="cs-figure-lg tabular-nums" style={{ fontSize: '1rem', color: 'var(--color-text-primary)' }}>
            {renderValue(method.value)}
          </span>
          {method.attested ? (
            <span
              className="cs-chip inline-flex items-center gap-1"
              style={{ color: 'var(--color-verified)' }}
              title={method.attested_by ? `Attested by ${method.attested_by}` : 'Attested'}
            >
              <CheckCircle2 className="h-3.5 w-3.5" />
              Attested{method.attested_by ? ` · ${method.attested_by}` : ''}
            </span>
          ) : (
            <span className="cs-chip" style={{ color: 'var(--color-text-dim)' }}>Not attested</span>
          )}
        </div>
      </div>
      {canExpand && expanded && (
        <div className="px-5 py-4" style={{ borderTop: '1px solid var(--color-border-faint)', background: 'var(--color-bg-base)' }}>
          <MethodDetailTable
            engineId={method.source_engine as string}
            details={details as EngineOutput}
            reportId={reportId}
            canAttest={canAttest}
          />
        </div>
      )}
    </div>
  );
}

function Section({
  title,
  subtitle,
  icon,
  methods,
  methodDetails,
  testid,
  reportId,
  canAttest,
}: {
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  methods: MethodologyResult[];
  methodDetails: Record<string, EngineOutput>;
  testid: string;
  reportId?: string;
  canAttest?: boolean;
}) {
  if (methods.length === 0) return null;
  const sorted = [...methods].sort(
    (a, b) => POSTURE_ORDER[a.posture] - POSTURE_ORDER[b.posture],
  );
  return (
    <section className="mb-8" data-testid={testid}>
      <div className="mb-3 flex items-center gap-2">
        <span className="inline-flex" style={{ color: 'var(--color-text-muted)' }}>{icon}</span>
        <div>
          <h2 className="cs-h2">{title}</h2>
          <p className="cs-meta">{subtitle}</p>
        </div>
      </div>
      <div
        className="overflow-hidden rounded-[2px]"
        style={{ border: '1px solid var(--color-border)', background: 'var(--color-bg-surface)' }}
      >
        {sorted.map((m) => (
          <MethodRow
            key={m.method_id}
            method={m}
            details={m.source_engine ? methodDetails[m.source_engine] : undefined}
            reportId={reportId}
            canAttest={canAttest}
          />
        ))}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export interface MethodologyViewContentProps {
  reportId?: string;
  showHeader?: boolean;
}

/**
 * MethodologyViewContent — the methodology-comparison body, parameterized so it
 * can render bare (no page header / back-nav) inside the accountant Case
 * Workspace Defense stage (extract-content pattern), while the standalone
 * /dashboard/accountant/methodology/:reportId route keeps its header via the
 * thin default-export wrapper below. Identical behavior, data flow, and
 * data-testids; the only new seam is { reportId?, showHeader? }.
 */
export function MethodologyViewContent({
  reportId: reportIdProp,
  showHeader = true,
}: MethodologyViewContentProps) {
  const { reportId: reportIdParam } = useParams<{ reportId: string }>();
  const reportId = reportIdProp ?? reportIdParam;
  const navigate = useNavigate();

  // Only an accountant licenses the carrier exhibit's "reasonableness check"
  // wording via a variance attribution (backend gate is accountant-only).
  const role = useWorkspaceRole();
  const canAttest =
    role === 'accountant_internal' ||
    role === 'accountant_external' ||
    role === 'admin';
  const canon = useCanonicalNet(reportId);

  const [data, setData] = useState<MethodologyResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!reportId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setData(await getMethodology(reportId));
    } catch (err: any) {
      setError(err?.message || 'Failed to load methodology slate');
    } finally {
      setLoading(false);
    }
  }, [reportId]);

  useEffect(() => {
    load();
  }, [load]);

  const biMethods = (data?.methods ?? []).filter((m) => m.category === 'BI');
  const leverMethods = (data?.methods ?? []).filter((m) => m.category === 'coverage_lever');
  const propertyMethods = (data?.methods ?? []).filter((m) => m.category === 'property');
  const hasAnyMethod = (data?.methods?.length ?? 0) > 0;
  const methodDetails = data?.method_details ?? {};
  const coverageAdjustments = data?.coverage_adjustments ?? {};
  const sel = data?.selection;

  return (
    <div className={showHeader ? 'min-h-screen' : ''} style={{ background: 'var(--color-bg-base)' }} data-testid="methodology-view">
      {showHeader && (
        <div className="px-6 py-4" style={{ borderBottom: '1px solid var(--color-border)', background: 'var(--color-bg-surface)' }}>
          <div className="mx-auto max-w-5xl">
            <button
              onClick={() => navigate(`/dashboard/accountant/review/${reportId}`)}
              className="cs-body mb-3 flex items-center gap-2"
              style={{ color: 'var(--color-text-muted)' }}
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Engine Review
            </button>
            <h1 className="cs-display">
              Methodology Comparison
            </h1>
            <p className="cs-meta mt-1">
              Every loss methodology this claim supports — cited, posture-tagged, and
              attestable. Compare the defensible range; attest each method on its engine card.
            </p>
          </div>
        </div>
      )}

      <div className="mx-auto max-w-5xl px-6 py-6">
        {loading && (
          <div className="cs-body flex items-center gap-2" style={{ color: 'var(--color-text-muted)' }}>
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading methodology slate…
          </div>
        )}

        {!loading && error && (
          <div
            className="cs-body rounded-[2px] px-5 py-4"
            style={{
              border: '1px solid var(--color-border)',
              borderLeft: '2px solid var(--color-blocker)',
              background: 'var(--color-bg-elevated)',
              color: 'var(--color-text-secondary)',
            }}
            data-testid="methodology-error"
          >
            <p style={{ fontWeight: 500, color: 'var(--color-blocker)' }}>Could not load methodologies</p>
            <p className="mt-1">{error}</p>
            <button
              onClick={load}
              className="cs-chip mt-3 rounded-[2px] px-3 py-1.5"
              style={{
                border: '1px solid var(--color-border)',
                background: 'var(--color-bg-surface)',
                color: 'var(--color-text-secondary)',
              }}
            >
              Retry
            </button>
          </div>
        )}

        {!loading && !error && data && (
          <>
            {/* Stale-engine warning — a selection must not sit on stale math. */}
            {Array.isArray(data.stale_engines) && data.stale_engines.length > 0 && (
              <div
                className="cs-body mb-6 flex items-start gap-2 rounded-[2px] px-4 py-3"
                style={{
                  border: '1px solid var(--color-border)',
                  borderLeft: '2px solid var(--color-needs-human)',
                  background: 'var(--color-bg-elevated)',
                  color: 'var(--color-text-secondary)',
                }}
                data-testid="methodology-stale-banner"
              >
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" style={{ color: 'var(--color-needs-human)' }} />
                <span>
                  Stale engine output: <strong>{data.stale_engines.join(', ')}</strong>. Re-run
                  these engines before relying on the figures below.
                </span>
              </div>
            )}

            {/* Selection summary — the supportable position. */}
            {sel && hasAnyMethod && (
              <div
                className="mb-8 rounded-[2px] p-5"
                style={{ border: '1px solid var(--color-border)', background: 'var(--color-bg-surface)' }}
                data-testid="methodology-selection-summary"
              >
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="cs-h2">
                    Supportable position
                  </h2>
                  <span
                    className="cs-chip rounded-[2px] border px-2.5 py-0.5"
                    style={{
                      borderColor: 'var(--color-border-sub)',
                      background: 'var(--color-bg-elevated)',
                      color: 'var(--color-text-muted)',
                    }}
                  >
                    {sel.profile.replace(/_/g, ' ')}
                  </span>
                </div>
                {/* Canonical net — headline (unconditional slot) */}
                <div className="mb-4" data-testid="methodology-net-headline">
                  <p className="cs-label">Net supported claim</p>
                  {canon.loading ? (
                    <p className="cs-figure-lg tabular-nums" style={{ color: 'var(--color-text-dim)', fontWeight: 700 }} data-testid="methodology-net-loading">
                      Loading…
                    </p>
                  ) : canon.net != null ? (
                    <>
                      <p className="cs-figure-lg tabular-nums" style={{ color: 'var(--color-text-primary)', fontWeight: 700 }}>
                        {renderValue(canon.net)}
                      </p>
                      <p className="cs-meta" style={{ color: 'var(--color-text-dim)' }}>
                        after policy deductions · binding figure
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="cs-figure-lg tabular-nums" style={{ color: 'var(--color-text-dim)', fontWeight: 700 }}>
                        {renderValue(null)}
                      </p>
                      <p className="cs-meta" style={{ color: 'var(--color-text-dim)' }}>
                        Canonical net unavailable
                      </p>
                    </>
                  )}
                  {canon.electedMethod && canon.electedMethod !== 'reconciled' && (
                    <p className="cs-meta mt-1" style={{ color: 'var(--color-text-dim)' }}>
                      Basis: {humanizeMethodId(canon.electedMethod)} method (attorney-elected)
                    </p>
                  )}
                </div>
                <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  {/* Floor — hidden when it duplicates the headline method so the
                      same figure is never shown twice, or when there is no floor value. */}
                  {sel.floor?.value != null && !(sel.headline && sel.floor.method_id === sel.headline.method_id) && (
                    <div>
                      <dt className="cs-label">
                        Bottom-up BI loss (pre-deduction)
                      </dt>
                      <dd className="cs-figure-lg mt-1 tabular-nums" style={{ fontSize: '1.125rem', color: 'var(--color-text-secondary)' }}>
                        {renderValue(sel.floor.value)}
                      </dd>
                      {sel.floor && (
                        <p className="cs-meta mt-0.5" style={{ color: 'var(--color-text-dim)' }}>{sel.floor.label}</p>
                      )}
                    </div>
                  )}
                  <div>
                    <dt className="cs-label">
                      {sel.headline?.method_id === 'bi_top_down'
                        ? 'Top-down cross-check'
                        : sel.headline?.method_id === 'bi_bottom_up'
                        ? 'Bottom-up BI loss (pre-deduction)'
                        : sel.headline?.label || 'Supportable total (pre-deduction)'}
                    </dt>
                    <dd className="cs-figure-lg mt-1 tabular-nums" style={{ fontSize: '1.125rem', color: 'var(--color-text-secondary)' }}>
                      {renderValue(sel.supportable_total)}
                    </dd>
                    {sel.headline && (
                      <p className="cs-meta mt-0.5" style={{ color: 'var(--color-text-dim)' }}>{sel.headline.label}</p>
                    )}
                  </div>
                  <div>
                    <dt className="cs-label">
                      With conditional levers
                    </dt>
                    <dd className="cs-figure-lg mt-1 tabular-nums" style={{ fontSize: '1.125rem', color: 'var(--color-text-secondary)' }}>
                      {renderValue(sel.with_levers_total)}
                    </dd>
                    <p className="cs-meta mt-0.5" style={{ color: 'var(--color-text-dim)' }}>
                      Levers reported separately — never silently anchored.
                    </p>
                  </div>
                </dl>
              </div>
            )}

            {/* Empty state — engines haven't produced output yet. */}
            {!hasAnyMethod && (
              <div
                className="rounded-[2px] border border-dashed px-6 py-12 text-center"
                style={{ borderColor: 'var(--color-border-sub)', background: 'var(--color-bg-surface)' }}
                data-testid="methodology-empty"
              >
                <div className="flex justify-center" style={{ color: 'var(--color-text-dim)' }}>
                  <BookOpen className="h-8 w-8" />
                </div>
                <h3 className="cs-h3 mt-3">
                  No methodologies yet
                </h3>
                <p className="cs-meta mx-auto mt-1 max-w-md">
                  The deterministic engines haven't produced output for this claim yet.
                  Run the engine pipeline from Engine Review, then return here to compare
                  the supportable loss methodologies.
                </p>
              </div>
            )}

            <Section
              title="Business interruption methodologies"
              subtitle="Each reads from a deterministic engine — compare the conservative-to-aggressive range."
              icon={<Scale className="h-5 w-5" />}
              methods={biMethods}
              methodDetails={methodDetails}
              testid="methodology-section-bi"
              reportId={reportId}
              canAttest={canAttest}
            />
            <Section
              title="Coverage levers"
              subtitle="Conditional add-ons (e.g. Extended Period of Indemnity) — reported separately, never folded into the supportable total."
              icon={<BookOpen className="h-5 w-5" />}
              methods={leverMethods}
              methodDetails={methodDetails}
              testid="methodology-section-levers"
            />
            <Section
              title="First-party property methodologies"
              subtitle="Property valuation bases (RCV / ACV / recoverable depreciation and policy add-ons)."
              icon={<Building2 className="h-5 w-5" />}
              methods={propertyMethods}
              methodDetails={methodDetails}
              testid="methodology-section-property"
            />

            {Object.keys(coverageAdjustments).length > 0 && (
              <section className="mb-8" data-testid="methodology-section-adjustments">
                <div className="mb-3 flex items-center gap-2">
                  <span className="inline-flex" style={{ color: 'var(--color-text-muted)' }}>
                    <AlertTriangle className="h-5 w-5" />
                  </span>
                  <div>
                    <h2 className="cs-h2">
                      Coverage caps &amp; adjustments
                    </h2>
                    <p className="cs-meta">
                      Caps, offsets, and defensibility checks that constrain or challenge the
                      loss methodologies above.
                    </p>
                  </div>
                </div>
                <div className="space-y-4">
                  {ADJUSTMENT_ORDER.filter((eid) => coverageAdjustments[eid]).map((eid) => (
                    <CoverageAdjustmentPanel
                      key={eid}
                      engineId={eid}
                      output={coverageAdjustments[eid]}
                    />
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/** Standalone route wrapper — header shown. */
export default function AccountantMethodologyView() {
  return <MethodologyViewContent showHeader />;
}
